import { type ComponentType, type ReactNode, useState } from 'react';
import { Navigate } from 'react-router-dom';

import constate from 'constate';

import { ToggleFeatureToolbar } from '@spacefill/uikit/src/components/ToggleFeatureToolbar';

import { getEnvironmentVariable } from '../GetEnvVar';
import { AvailableToggleFeatures, ENV_LIST, getEnabledFeaturesByEnv } from './availableToggleFeatures';

const useFeatureToggling = () => {
    const currentEnv = getEnvironmentVariable('envName');

    const ENABLED_FEATURES: AvailableToggleFeatures[] = getEnabledFeaturesByEnv(currentEnv as ENV_LIST);

    const FEATURE_TOGGLING_IS_ENABLED = getEnvironmentVariable('featureTogglingEnabled');

    const featureSettings: Partial<Record<AvailableToggleFeatures, boolean>> = {};

    // Init all toggle to false
    for (const key of Object.values(AvailableToggleFeatures)) {
        featureSettings[key as unknown as AvailableToggleFeatures] = false;
    }

    ENABLED_FEATURES.forEach((featureName) => {
        featureSettings[featureName] = true;
    });

    if (FEATURE_TOGGLING_IS_ENABLED) {
        const queryParams = new URLSearchParams(window.location.search);

        // Get feature flags from URL and override values from featureSettings AND set to localStorage
        // Then get remaining values from localStorage and overwrite featureSettings
        for (const [featureKey] of Object.entries(featureSettings)) {
            const keyValue = queryParams.get(featureKey);
            const key = featureKey as unknown as AvailableToggleFeatures;

            if (keyValue === '1') {
                featureSettings[key] = true;
                window.localStorage.setItem(`toggled_feature_${featureKey}`, String(true));
            } else if (keyValue === '0') {
                featureSettings[key] = false;
                window.localStorage.setItem(`toggled_feature_${featureKey}`, String(false));
            } else {
                const v = window.localStorage.getItem(`toggled_feature_${featureKey}`);
                if (v !== null) {
                    featureSettings[key] = v === 'true';
                }
            }
        }
    }

    const [features, setFeatures] = useState(featureSettings);
    const toggleFeature = (featName: AvailableToggleFeatures) =>
        setFeatures((prevFeatures) => {
            const newFlagValue = !prevFeatures[featName];

            if (FEATURE_TOGGLING_IS_ENABLED) {
                window.localStorage.setItem('toggled_feature_' + featName, String(newFlagValue));
            }

            return {
                ...prevFeatures,
                [featName]: newFlagValue,
            };
        });

    return { features, toggleFeature };
};

const [FeatureTogglingProvider, useFeatureTogglingContext] = constate(useFeatureToggling);

const FeatureTogglingToolbar = () => {
    const FEATURE_TOGGLING_IS_ENABLED = getEnvironmentVariable('featureTogglingEnabled');
    const FEATURE_TOGGLING_IS_NOT_ENABLED = !FEATURE_TOGGLING_IS_ENABLED;

    const { features, toggleFeature } = useFeatureTogglingContext(); // cannot use hook in conditional block :'(

    const [toolbarIsOpen, setToolbarIsOpen] = useState(false);
    if (FEATURE_TOGGLING_IS_NOT_ENABLED) {
        return null;
    }

    return (
        <ToggleFeatureToolbar
            features={features as Record<string, boolean>}
            toggleFeature={toggleFeature as unknown as (featureName: string) => unknown}
            toolbarIsOpen={toolbarIsOpen}
            setToolbarIsOpen={setToolbarIsOpen}
        />
    );
};

interface FeatureProps {
    name: AvailableToggleFeatures;
    children: ReactNode;
    InactiveComponent?: ComponentType;
}

const Feature = ({ name, children, InactiveComponent = () => null }: FeatureProps) => {
    const { features } = useFeatureTogglingContext();
    if (features === undefined) {
        console.error("FeatureTogglingProvider isn't initialized");
        return <InactiveComponent />;
    }
    return features[name] === true ? children : <InactiveComponent />;
};

interface FeatureTogglingElementProps {
    featureName: AvailableToggleFeatures;
    whenFeatureIsDisabledRender?: ReactNode;
    whenFeatureIsDisabledRedirectTo?: string;
    children: ReactNode;
}

const FeatureTogglingElement = ({
    featureName,
    whenFeatureIsDisabledRender,
    whenFeatureIsDisabledRedirectTo,
    children,
}: FeatureTogglingElementProps) => {
    const { features } = useFeatureTogglingContext();

    if (featureName === undefined || features[featureName] === true) {
        return children;
    }
    if (whenFeatureIsDisabledRender) {
        return whenFeatureIsDisabledRender;
    }
    if (whenFeatureIsDisabledRedirectTo) {
        return <Navigate to={whenFeatureIsDisabledRedirectTo} />;
    }
    return null;
};

export { FeatureTogglingProvider, useFeatureTogglingContext, FeatureTogglingToolbar, Feature, FeatureTogglingElement };
